"use client";

/**
 * Artwork for a marketplace listing: its own image → the artwork of the page
 * it links to (scraped once by /api/marketplace/item-artwork) → generated,
 * animated vector art in the theme's colours with the product name, the
 * pattern picked to suit what the product is (Gordon 2026-10-31).
 */

import { useEffect, useState } from "react";
import { GenerativeArtistAvatar, PATTERN_NAMES } from "@/components/media/generative-artist-avatar";
import { cn } from "@/lib/utils";

const PATTERNS: [RegExp, (typeof PATTERN_NAMES)[number]][] = [
  [/kick|snare|drum|beat ?machine|percussion|808|groove|sampler/i, "Drum Pads"],
  [/scope|analy[sz]|meter|detector|spectrum|tuner/i, "Oscilloscope"],
  [/delay|echo|reverb|chorus|flanger/i, "Frequency Rings"],
  [/bass|synth|oscillat|wavetable|sound design|generative/i, "Sound Wave"],
  [/piano|keys|chord|scale|midi|harmony/i, "Piano Keys"],
  [/\bdaw\b|studio|mix|master|console|fader/i, "Mixer Faders"],
  [/vinyl|record|pressing|turntable/i, "Vinyl Record"],
  [/cassette|tape/i, "Cassette Reels"],
  [/lesson|course|teach|coach|tutor|class/i, "Treble Clef"],
  [/speaker|monitor|headphone|amp/i, "Speaker Cone"],
  [/mic|vocal|voice|sing/i, "EQ Bars"],
];

export function itemPattern(text: string): number {
  for (const [re, name] of PATTERNS) if (re.test(text)) return PATTERN_NAMES.indexOf(name);
  return -1; // seeded from the name
}

export function ownItemImage(item: any): string {
  // The first IMAGE — a video or audio file isn't a picture.
  const media: any[] = Array.isArray(item?.media) ? item.media : [];
  const m = media.find(x => typeof x === "string" || !x?.type || x.type === "image");
  return (typeof m === "string" ? m : m?.url) || "";
}

export function ItemArtwork({ item, className }: { item: any; className?: string }) {
  const [url, setUrl] = useState<string>(ownItemImage(item) || item?.scrapedImageUrl || "");
  useEffect(() => {
    if (url || item?.scrapedAt || !item?.itemId) return;
    let alive = true;
    fetch(`/api/marketplace/item-artwork?id=${encodeURIComponent(item.itemId)}`)
      .then(r => r.json())
      .then(d => { if (alive && d?.imageUrl) setUrl(d.imageUrl); })
      .catch(() => {});
    return () => { alive = false; };
  }, [item?.itemId, item?.scrapedAt, url]);

  // A dead link falls back to the generated art instead of a broken icon.
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [url]);
  if (url && !failed) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={url} alt={item?.title || ""} loading="lazy" onError={() => setFailed(true)} className={cn("h-full w-full object-cover", className)} />;
  }
  const text = [item?.title, item?.description, item?.categoryKey, item?.type, ...(Array.isArray(item?.tags) ? item.tags : [])].filter(Boolean).join(" ");
  return <GenerativeArtistAvatar name={item?.title || "?"} patternOverride={itemPattern(text)} className={cn("h-full w-full", className)} animate />;
}
